// src/i18n.js
// Lightweight i18n for EN / 中文. No external dependency. The strings
// dictionary is keyed by flat dot-paths; the current locale is persisted
// in a cookie so the choice survives across sessions.
//
// On locale change:
//   - document.documentElement.lang is updated (helps screen readers
//     and browser translation features)
//   - any subscribers registered via onLocaleChange are notified so
//     they can re-render

import { setCookie, getCookie } from './cookie.js';

const STORAGE_KEY = 'helix.locale';

// Default locale: auto-detect from browser on first visit. If the
// navigator language starts with 'zh' (Simplified or Traditional
// Chinese), default to zh. Otherwise default to en. The user's
// manual choice via the toggle overrides this and is persisted.
function detectDefaultLocale() {
  try {
    const stored = getCookie(STORAGE_KEY);
    if (stored) return stored;
  } catch { /* cookie may be blocked */ }
  const lang = (navigator.language || 'en').toLowerCase();
  if (lang.startsWith('zh')) return 'zh';
  return 'en';
}

const STRINGS = {
  en: {
    'app.title': 'Helix Composer — Free Online Orchestration Simulator | Chinese &amp; Western Instruments',
    'app.heading': 'Helix Composer — Free Online Orchestration Simulator',
    'app.tagline': 'Layer Chinese and Western instruments into a living ensemble',

    'splash.welcome': 'Welcome to Helix Composer',
    'splash.intro': 'Build a layered ensemble of Chinese and Western instruments and hear it play in real time.',
    'splash.step1': 'Pick instruments from the panel on the left, or load a demo preset below.',
    'splash.step2': 'Click notes into the timeline — or press keys A–L on your computer keyboard.',
    'splash.step3': 'Press <strong>Play</strong> to listen, or the red <strong>Record</strong> button to capture your performance.',
    'splash.presetHint': 'Demo presets (String quartet, Chinese ensemble, Flute · Harp · Piano) appear in the instrument panel after you start.',
    'splash.cacheExplain': 'First visit downloads ~28 MB of samples so they can play offline. Subsequent visits are instant.',
    'splash.cacheStatus': 'Preparing samples…',
    'splash.cacheReady': 'Samples ready. Click to start composing.',
    'splash.cacheFailed': 'Sample download failed',
    'splash.cacheDownloading': 'Downloading samples… {0} / 28 MB ({1}%)',
    'splash.startButton': "Let's go",
    'splash.dontShowAgain': "Don't show this at launch next time",
    'splash.audioHint': 'Click to enable audio (browsers require a user gesture).',

    'countdown.label': 'Recording in',

    'donate.message': 'Your support are greatly appreciated and will help me continue this work.',
    'donate.scanLabel': 'Scan to support',
    'donate.wechatMsg': '请扫码关注公众号获得更多资讯',

    'panel.instruments': 'Instruments',
    'panel.presets': 'Demo presets',
    'panel.mixer': 'Mixer',
    'panel.notes': 'Notes',
    'panel.clipLibrary': 'Clip Library',
    'panel.piano': 'Piano',
    'panel.donate': 'Support',

    'footer.poweredBy': 'Powered by <a href="{0}" target="_blank" rel="noopener noreferrer nofollow">Inotech Solution</a>',

    'midi.import': 'Import MIDI',
    'midi.export': 'Export MIDI',
    'midi.clear': 'Clear all',
    'midi.confirmClear': 'Clear all tracks and notes?',
    'midi.trackFallback': 'Track {0}',

    'piano.bHint': 'Shift+key = sustain',
    'piano.sustain': 'SUS',
    'piano.full': 'Full',
    'piano.compact': 'Compact',
    'piano.octDown': 'Oct −',
    'piano.octUp': 'Oct +',
    'piano.titleFull': 'Switch to full layout',
    'piano.titleCompact': 'Switch to compact layout',
    'piano.titleOctDown': 'Shift octave down ( [ )',
    'piano.titleOctUp': 'Shift octave up ( ] )',
    'piano.titleSustain': 'Tap-to-sustain (when ON, tap a key to start ringing, tap again to release)',
    'piano.noTrackSelected': 'Click a track tab on the left to enable the piano',

    'mixer.empty': 'No tracks yet. Add an instrument to start.',
    'mixer.tracks': '{0} tracks',
    'mixer.removeTitle': 'Remove track',

    'notes.empty': 'Click a track tab on the left to edit its notes.',
    'notes.colIndex': '#',
    'notes.colPitch': 'Pitch',
    'notes.colTime': 'Time',
    'notes.colDuration': 'Duration',
    'notes.colVelocity': 'Velocity',
    'notes.colActions': '',
    'notes.addBtn': '+ Add note',
    'notes.deleteTitle': 'Delete note',
    'notes.pitchTitle': 'MIDI pitch (0–127)',
    'notes.timeTitle': 'Time in seconds',
    'notes.durationTitle': 'Duration in seconds',
    'notes.velocityTitle': 'Velocity (0–1)',
    'notes.count': '{0} notes',

    'transport.play': 'Play / Pause (Space)',
    'transport.stop': 'Stop — cancel schedule, return to start',
    'transport.loop': 'Loop',
    'transport.record': 'Record — capture piano-key presses as notes on the selected track (Alt+R)',
    'transport.selectTrackFirst': 'Select a track first',
    'transport.recordingIn': 'Recording in',
    'transport.bpm': 'BPM',
    'transport.reverb': 'Reverb wet',
    'transport.humanize': 'Humanize',

    'seq.empty': 'Pick an instrument on the left to add a track',
    'seq.clearAll': 'Clear all tracks and notes?',

    'lang.label': 'Language',
  },

  zh: {
    'app.title': 'Helix Composer — 免费在线管弦乐模拟器 | 中西乐器融合',
    'app.heading': 'Helix Composer — 免费在线管弦乐模拟器',
    'app.tagline': '将中西方乐器融合成生动的合奏',

    'splash.welcome': '欢迎使用 Helix Composer',
    'splash.intro': '用多层中西方乐器构建合奏，实时聆听您创作的音乐。',
    'splash.step1': '从左侧面板选择乐器，或加载下方的示例预设。',
    'splash.step2': '在时间轴上点击添加音符 — 也可以按键盘 A–L 键演奏。',
    'splash.step3': '点击 <strong>Play</strong> 聆听，或点击红色 <strong>Record</strong> 按钮录制您的演奏。',
    'splash.presetHint': '开始后，示例预设（弦乐四重奏、中国民乐、长笛·竖琴·钢琴）将显示在乐器面板中。',
    'splash.cacheExplain': '首次访问将下载约 28 MB 的采样音色，以便离线使用。后续访问即刻加载。',
    'splash.cacheStatus': '正在准备音色…',
    'splash.cacheReady': '音色已就绪。点击开始作曲。',
    'splash.cacheFailed': '音色下载失败',
    'splash.cacheDownloading': '正在下载音色… {0} / 28 MB ({1}%)',
    'splash.startButton': '开始作曲',
    'splash.dontShowAgain': '启动时不再显示',
    'splash.audioHint': '点击以启用音频（浏览器需要用户交互）。',

    'countdown.label': '录制倒计时',

    'donate.message': '非常感谢您的捐赠，这将帮助我继续这项工作。',
    'donate.scanLabel': '扫码捐赠',
    'donate.wechatMsg': '请扫码关注公众号获得更多资讯',

    'panel.instruments': '乐器',
    'panel.presets': '示例预设',
    'panel.mixer': '混音器',
    'panel.notes': '音符',
    'panel.clipLibrary': '片段库',
    'panel.piano': '钢琴',
    'panel.donate': '支持',

    'footer.poweredBy': '由 <a href="{0}" target="_blank" rel="noopener noreferrer nofollow">Inotech Solution</a> 提供支持',

    'midi.import': '导入 MIDI',
    'midi.export': '导出 MIDI',
    'midi.clear': '全部清除',
    'midi.confirmClear': '确定要清除所有轨道和音符吗？',
    'midi.trackFallback': '轨道 {0}',

    'piano.bHint': 'Shift+键 = 延音',
    'piano.sustain': '延音',
    'piano.full': '完整',
    'piano.compact': '紧凑',
    'piano.octDown': '八度 −',
    'piano.octUp': '八度 +',
    'piano.titleFull': '切换到完整布局',
    'piano.titleCompact': '切换到紧凑布局',
    'piano.titleOctDown': '降低八度 ( [ )',
    'piano.titleOctUp': '升高八度 ( ] )',
    'piano.titleSustain': '点击延音（开启后，点击琴键开始发声，再次点击停止）',
    'piano.noTrackSelected': '点击左侧轨道标签以启用钢琴',

    'mixer.empty': '暂无轨道。请添加乐器开始创作。',
    'mixer.tracks': '{0} 个轨道',
    'mixer.removeTitle': '删除轨道',

    'notes.empty': '点击左侧轨道标签编辑其音符。',
    'notes.colIndex': '#',
    'notes.colPitch': '音高',
    'notes.colTime': '时间',
    'notes.colDuration': '时长',
    'notes.colVelocity': '力度',
    'notes.colActions': '',
    'notes.addBtn': '+ 添加音符',
    'notes.deleteTitle': '删除音符',
    'notes.pitchTitle': 'MIDI 音高 (0–127)',
    'notes.timeTitle': '时间（秒）',
    'notes.durationTitle': '时长（秒）',
    'notes.velocityTitle': '力度 (0–1)',
    'notes.count': '{0} 个音符',

    'transport.play': '播放 / 暂停 (空格)',
    'transport.stop': '停止 — 取消排程，回到起点',
    'transport.record': '录制 — 将钢琴按键捕获为选中轨道的音符 (Alt+R)',
    'transport.loop': '循环',
    'transport.selectTrackFirst': '请先选择一个轨道',
    'transport.recordingIn': '录制倒计时',
    'transport.bpm': '拍速 (BPM)',
    'transport.reverb': '混响湿度',
    'transport.humanize': '人性化',

    'seq.empty': '从左侧选择一个乐器以添加轨道',
    'seq.clearAll': '确定要清除所有轨道和音符吗？',

    'lang.label': '语言',
  }
};

let currentLocale = detectDefaultLocale();

export function getLocale() { return currentLocale; }

export function setLocale(loc) {
  if (loc !== 'en' && loc !== 'zh') return;
  if (loc === currentLocale) return;
  currentLocale = loc;
  try { setCookie(STORAGE_KEY, loc); } catch { /* ignore */ }
  document.documentElement.lang = loc === 'zh' ? 'zh-CN' : 'en';
  for (const fn of localeListeners) fn(loc);
  refreshToggleUi();
}

export function t(key, ...args) {
  const s = STRINGS[currentLocale]?.[key] ?? STRINGS.en[key] ?? key;
  return args.length ? s.replace(/\{(\d+)\}/g, (_, i) => args[+i] ?? '') : s;
}

const localeListeners = new Set();
export function onLocaleChange(fn) {
  localeListeners.add(fn);
  return () => localeListeners.delete(fn);
}

function refreshToggleUi() {
  for (const toggle of document.querySelectorAll('.lang-toggle')) {
    toggle.setAttribute('aria-checked', currentLocale === 'zh' ? 'true' : 'false');
    toggle.classList.toggle('is-active', currentLocale === 'zh');
    toggle.dataset.lang = currentLocale;
  }
}

// Delegated click handler for the language toggle — the entire
// sliding-thumb pill is the click target. Clicking toggles the locale.
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('.lang-toggle');
  if (!toggle) return;
  const next = toggle.dataset.lang === 'zh' ? 'en' : 'zh';
  setLocale(next);
});

// Apply <html lang> on initial load.
document.documentElement.lang = currentLocale === 'zh' ? 'zh-CN' : 'en';

// Sync the toggle's visual state with the detected locale. Deferred
// to DOMContentLoaded so the .lang-toggle element is guaranteed to
// be in the DOM when we try to update it. Without this, the toggle
// always shows EN on load even when the actual locale is zh, and the
// first click appears to do nothing (it switches zh→en but the visual
// was already EN).
function initToggleUi() {
  refreshToggleUi();
  document.removeEventListener('DOMContentLoaded', initToggleUi);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initToggleUi);
} else {
  refreshToggleUi();
}
